import { Button, Typography } from 'antd';
import {
	ClearOutlined,
	FilterOutlined,
	UpOutlined,
	DownOutlined,
} from '@ant-design/icons';
import type { FilterDefinition, UseFilterStateReturn } from '../types';
import { FilterRenderer } from '../components/FilterRenderer';

const { Text } = Typography;

interface FilterOption {
	value: string;
	label: string;
}

interface FilterOptionGroup {
	label: string;
	options: FilterOption[];
}

interface ResolvedOptionsMap {
	[filterKey: string]: FilterOption[] | undefined;
}

interface ResolvedOptionGroupsMap {
	[filterKey: string]: FilterOptionGroup[] | undefined;
}

export interface InlineFilterGroup {
	label: string;
	keys: string[];
}

interface Props {
	definitions: FilterDefinition[];
	filterState: UseFilterStateReturn;
	resolvedOptions?: ResolvedOptionsMap;
	resolvedOptionGroups?: ResolvedOptionGroupsMap;
	onResetAll: () => void;
	groups?: InlineFilterGroup[];
	expanded?: boolean;
	onToggleExpanded?: () => void;
}

// ─── Single filter cell ──────────────────────────────────────────────────────

const FilterCell = ({
	def,
	filterState,
	resolvedOptions,
	resolvedOptionGroups,
}: {
	def: FilterDefinition;
	filterState: UseFilterStateReturn;
	resolvedOptions?: FilterOption[];
	resolvedOptionGroups?: FilterOptionGroup[];
}) => (
	<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		<div
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: 8,
			}}
		>
			<Text
				type="secondary"
				style={{ fontSize: 11, lineHeight: '14px', whiteSpace: 'nowrap' }}
			>
				{def.label}
			</Text>
			{def.resettable !== false && (
				<Button
					type="link"
					size="small"
					style={{ fontSize: 10, padding: 0, height: 'auto' }}
					onClick={() => filterState.resetFilter(def.key)}
				>
					Reset
				</Button>
			)}
		</div>
		<FilterRenderer
			definition={def}
			filterState={filterState}
			resolvedOptions={resolvedOptions}
			resolvedOptionGroups={resolvedOptionGroups}
			compact={true}
		/>
	</div>
);

// ─── Component ───────────────────────────────────────────────────────────────

export const TabletInlineFilters = ({
	definitions,
	filterState,
	resolvedOptions = {},
	resolvedOptionGroups = {},
	onResetAll,
	groups = [],
	expanded = true,
	onToggleExpanded,
}: Props) => {
	const visibleFilters = definitions.filter(
		(def) =>
			def.contexts.includes('tablet') && filterState.isVisible(def.key)
	);

	if (visibleFilters.length === 0) return null;

	// Build a set of keys that belong to any group
	const groupedKeys = new Set(groups.flatMap((g) => g.keys));

	// Ungrouped filters: visible filters not in any group
	const ungroupedFilters = visibleFilters.filter(
		(def) => !groupedKeys.has(def.key)
	);

	// Split ungrouped into before/after groups based on definition order
	const firstGroupedIndex =
		groups.length > 0
			? definitions.findIndex((d) => groupedKeys.has(d.key))
			: -1;

	const ungroupedBefore =
		firstGroupedIndex === -1
			? ungroupedFilters
			: ungroupedFilters.filter(
					(def) =>
						definitions.findIndex((d) => d.key === def.key) <
						firstGroupedIndex
			  );

	const ungroupedAfter =
		firstGroupedIndex === -1
			? []
			: ungroupedFilters.filter(
					(def) =>
						definitions.findIndex((d) => d.key === def.key) >=
						firstGroupedIndex
			  );

	// For each group, resolve which filters are actually visible
	const resolvedGroups = groups
		.map((group) => ({
			...group,
			filters: group.keys
				.map((key) => visibleFilters.find((f) => f.key === key))
				.filter(Boolean) as FilterDefinition[],
		}))
		.filter((g) => g.filters.length > 0);

	return (
		<div
			style={{
				background: '#fff',
				border: '1px solid #D9D9D9',
				borderRadius: 4,
			}}
		>
			{/* ── Header row: clickable to collapse/expand ────────── */}
			<div
				onClick={onToggleExpanded}
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '4px 10px',
					borderBottom: expanded ? '1px solid #f0f0f0' : 'none',
					cursor: 'pointer',
				}}
			>
				<Text type="secondary" style={{ fontSize: 12 }}>
					<FilterOutlined style={{ marginRight: 6 }} />
					Filters
					{expanded ? (
						<UpOutlined style={{ marginLeft: 6, fontSize: 10 }} />
					) : (
						<DownOutlined style={{ marginLeft: 6, fontSize: 10 }} />
					)}
				</Text>
				<Button
					type="link"
					size="small"
					icon={<ClearOutlined />}
					onClick={(e) => {
						e.stopPropagation();
						onResetAll();
					}}
					style={{ fontSize: 12 }}
				>
					Reset All
				</Button>
			</div>

			{/* ── Scrollable filter row ───────────────────────────── */}
			{expanded && (
				<div
					style={{
						display: 'flex',
						alignItems: 'stretch',
						gap: 10,
						padding: '8px 10px',
						overflowX: 'auto',
						overflowY: 'hidden',
					}}
				>
					{/* ── Ungrouped filters (before groups) ──────────────── */}
					{ungroupedBefore.map((def) => (
						<div
							key={def.key}
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								flexShrink: 0,
								padding: '4px 8px',
								borderRight: '1px solid #f0f0f0',
								...(def.key === 'shape' ? { minWidth: 150 } : {}),
							}}
						>
							<FilterCell
								def={def}
								filterState={filterState}
								resolvedOptions={resolvedOptions[def.key]}
								resolvedOptionGroups={resolvedOptionGroups[def.key]}
							/>
						</div>
					))}

					{/* ── Grouped filters ─────────────────────────────────── */}
					{resolvedGroups.map((group) => (
						<div
							key={group.label}
							style={{
								display: 'flex',
								flexDirection: 'column',
								flexShrink: 0,
								padding: '4px 8px',
								borderRight: '1px solid #f0f0f0',
								gap: 4,
							}}
						>
							<Text
								strong
								style={{
									fontSize: 11,
									lineHeight: '14px',
									whiteSpace: 'nowrap',
									color: '#595959',
									borderBottom: '1px solid #f0f0f0',
									paddingBottom: 3,
								}}
							>
								{group.label}
							</Text>

							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: 8,
								}}
							>
								{group.filters.map((def) => (
									<div
										key={def.key}
										style={{
											flexShrink: 0,
											...(def.key === 'shape' ? { minWidth: 150 } : {}),
										}}
									>
										<FilterCell
											def={def}
											filterState={filterState}
											resolvedOptions={resolvedOptions[def.key]}
											resolvedOptionGroups={
												resolvedOptionGroups[def.key]
											}
										/>
									</div>
								))}
							</div>
						</div>
					))}

					{/* ── Ungrouped filters (after groups) ───────────────── */}
					{ungroupedAfter.map((def) => (
						<div
							key={def.key}
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								flexShrink: 0,
								padding: '4px 8px',
								borderRight: '1px solid #f0f0f0',
								...(def.key === 'shape' ? { minWidth: 150 } : {}),
							}}
						>
							<FilterCell
								def={def}
								filterState={filterState}
								resolvedOptions={resolvedOptions[def.key]}
								resolvedOptionGroups={resolvedOptionGroups[def.key]}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
};